import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '@/components/AdminNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { 
  GraduationCap, BookOpen, Users, Plus, UserPlus, CheckCircle, Award, 
  Download, Upload, FileText, Eye, Trash2, MessageSquare, ArrowLeft,
  Clock, AlertTriangle, AlertCircle, CheckCircle2, Calendar, Video, X, Bell
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminTraining = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Courses state
  const [courses, setCourses] = useState([]);
  const [crafts, setCrafts] = useState([]);
  const [selectedRequiredCourses, setSelectedRequiredCourses] = useState([]);
  
  // Classroom Learning state
  const [classroomStudents, setClassroomStudents] = useState([]);
  const [showStudentDialog, setShowStudentDialog] = useState(false);
  const [showAddExistingUserDialog, setShowAddExistingUserDialog] = useState(false);
  const [showPassDialog, setShowPassDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [studentForm, setStudentForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    password: '',
    craft: ''
  });
  
  // Toolbox Talks state
  const [toolboxTalks, setToolboxTalks] = useState([]);
  const [showCreateTalkDialog, setShowCreateTalkDialog] = useState(false);
  const [showUploadTalkDialog, setShowUploadTalkDialog] = useState(false);
  const [showViewTalkDialog, setShowViewTalkDialog] = useState(false);
  const [showToolboxSessionsDialog, setShowToolboxSessionsDialog] = useState(false);
  const [selectedTalk, setSelectedTalk] = useState(null);
  const [talkForm, setTalkForm] = useState({
    title: '',
    description: '',
    content: '',
    category: ''
  });
  const [selectedTalkPdf, setSelectedTalkPdf] = useState(null);
  const [uploadingTalk, setUploadingTalk] = useState(false);
  const [toolboxSessions, setToolboxSessions] = useState([]);

  // Certification Expiration state
  const [certExpirationData, setCertExpirationData] = useState(null);
  const [loadingCertExpiration, setLoadingCertExpiration] = useState(false);
  const [certExpirationFilter, setCertExpirationFilter] = useState('all');

  // Certificate file upload state
  const [showCertUploadDialog, setShowCertUploadDialog] = useState(false);
  const [selectedCertForUpload, setSelectedCertForUpload] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [uploadingCertFile, setUploadingCertFile] = useState(false);

  // Bulk certificate file upload state
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]); // [{file, matchedCert, matchConfidence}]
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ done: 0, total: 0 });

  // Expiration notification state
  const [sendingAlerts, setSendingAlerts] = useState(false);

  // Course Creator state
  const [showCreateCourseDialog, setShowCreateCourseDialog] = useState(false);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [companyCourses, setCompanyCourses] = useState([]);
  const [showEditCourseDialog, setShowEditCourseDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'Safety',
    content: '',
    duration: 30,
    imageUrl: '',
    videoUrl: '',
    videoFile: null,
    videoFileName: '',
    trainingType: 'company_private',
    certExpiry: '',
    requiredForJSA: false
  });
  const [quizQuestions, setQuizQuestions] = useState([
    { question: '', options: ['', '', '', ''], correctAnswer: 0 }
  ]);

  useEffect(() => {
    loadData();
    loadCompanyCourses();
  }, []);

  const loadData = async () => {
    try {
      const [coursesRes, craftsRes, usersRes, requiredCoursesRes] = await Promise.all([
        axiosInstance.get('/courses'),
        axiosInstance.get('/crafts'),
        axiosInstance.get('/admin/users'),
        axiosInstance.get('/admin/company/required-courses').catch(() => ({ data: null }))
      ]);
      
      setCourses(coursesRes.data);
      setCrafts(craftsRes.data);
      setUsers(usersRes.data);
      
      if (requiredCoursesRes.data?.courseIds) {
        setSelectedRequiredCourses(requiredCoursesRes.data.courseIds);
      }
      
      if (user.role === 'company_admin' || user.role === 'division_admin') {
        loadClassroomStudents();
        loadToolboxTalks();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t('adminToasts.FailedToLoadTrainingData'));
    } finally {
      setLoading(false);
    }
  };

  const loadClassroomStudents = async () => {
    try {
      const response = await axiosInstance.get('/admin/classroom/students');
      setClassroomStudents(response.data);
    } catch (error) {
      console.error('Failed to load classroom students:', error);
    }
  };

  const loadToolboxTalks = async () => {
    try {
      const response = await axiosInstance.get('/toolbox-talks');
      setToolboxTalks(response.data);
    } catch (error) {
      console.error('Failed to load toolbox talks:', error);
    }
  };

  // Load certification expiration data
  const loadCertExpirationData = async () => {
    setLoadingCertExpiration(true);
    try {
      const response = await axiosInstance.get('/analytics/certification-expiration');
      setCertExpirationData(response.data);
    } catch (error) {
      console.error('Failed to load certification expiration data:', error);
      toast.error(t('adminToasts.FailedToLoadCertificationExpirationData'));
    } finally {
      setLoadingCertExpiration(false);
    }
  };

  // Helper to get filtered certificates based on selected filter
  const getFilteredCertificates = () => {
    if (!certExpirationData) return [];
    switch (certExpirationFilter) {
      case 'expired':
        return certExpirationData.expired || [];
      case 'expiring_soon':
        return certExpirationData.expiringSoon || [];
      case 'expiring_later':
        return certExpirationData.expiringLater || [];
      case 'valid':
        return certExpirationData.valid || [];
      case 'no_expiry':
        return certExpirationData.noExpiry || [];
      case 'all':
      default:
        return [
          ...(certExpirationData.expired || []),
          ...(certExpirationData.expiringSoon || []),
          ...(certExpirationData.expiringLater || []),
          ...(certExpirationData.valid || []),
          ...(certExpirationData.noExpiry || [])
        ];
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // Get status badge for certificate
  const getStatusBadge = (status, daysUntilExpiry) => {
    switch (status) {
      case 'expired':
        return <Badge className="bg-red-500 text-white">Expired {Math.abs(daysUntilExpiry)} days ago</Badge>;
      case 'expiring_soon':
        return <Badge className="bg-orange-500 text-white">Expires in {daysUntilExpiry} days</Badge>;
      case 'expiring_later':
        return <Badge className="bg-yellow-500 text-white">Expires in {daysUntilExpiry} days</Badge>;
      case 'valid':
        return <Badge className="bg-green-500 text-white">Valid ({daysUntilExpiry} days)</Badge>;
      case 'no_expiry':
      default:
        return <Badge variant="outline">{t('adminUI.NoExpiry')}</Badge>;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  // Required Courses Handlers
  const handleToggleRequiredCourse = (courseId) => {
    setSelectedRequiredCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleSaveRequiredCourses = async () => {
    try {
      await axiosInstance.post('/admin/company/required-courses', {
        courseIds: selectedRequiredCourses
      });
      toast.success(t('adminToasts.RequiredCoursesUpdatedSuccessfully'));
    } catch (error) {
      console.error('Failed to save required courses:', error);
      toast.error(t('adminToasts.FailedToSaveRequiredCourses'));
    }
  };

  // Certificate file upload handler
  const handleCertFileUpload = async () => {
    if (!certFile || !selectedCertForUpload) return;
    setUploadingCertFile(true);
    try {
      const formData = new FormData();
      formData.append('file', certFile);
      const res = await axiosInstance.post(`/certificates/${selectedCertForUpload.id}/upload-file`, formData);
      toast.success(t('adminToasts.CertificateFileUploadedSuccessfully'));
      // Update the local data so UI reflects the change immediately
      if (certExpirationData) {
        const updateCert = (list) => list?.map(c => 
          c.id === selectedCertForUpload.id 
            ? { ...c, attachedFileId: res.data.attachedFileId, attachedFileName: res.data.attachedFileName } 
            : c
        );
        setCertExpirationData(prev => ({
          ...prev,
          expired: updateCert(prev.expired),
          expiringSoon: updateCert(prev.expiringSoon),
          expiringLater: updateCert(prev.expiringLater),
          valid: updateCert(prev.valid),
          noExpiry: updateCert(prev.noExpiry)
        }));
      }
      setShowCertUploadDialog(false);
      setCertFile(null);
      setSelectedCertForUpload(null);
    } catch (error) {
      console.error('Failed to upload certificate file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload certificate file');
    } finally {
      setUploadingCertFile(false);
    }
  };

  // Bulk upload helpers
  const getAllCertificates = () => {
    if (!certExpirationData) return [];
    return [
      ...(certExpirationData.expired || []),
      ...(certExpirationData.expiringSoon || []),
      ...(certExpirationData.expiringLater || []),
      ...(certExpirationData.valid || []),
      ...(certExpirationData.noExpiry || [])
    ];
  };

  const matchFileTocert = (filename, allCerts) => {
    const name = filename.replace(/\.[^/.]+$/, '').toLowerCase(); // strip extension
    const parts = name.replace(/[_\-\.]/g, ' ').split(/\s+/);
    let bestMatch = null;
    let bestScore = 0;

    for (const cert of allCerts) {
      let score = 0;
      const workerName = (cert.userName || '').toLowerCase();
      const courseName = (cert.courseName || '').toLowerCase();
      const certId = (cert.certificateId || '').toLowerCase();

      // Exact cert ID match
      if (name.includes(certId)) { score += 100; }
      // Worker name tokens
      const workerParts = workerName.split(/\s+/);
      for (const wp of workerParts) {
        if (wp.length > 1 && parts.some(p => p.includes(wp) || wp.includes(p))) score += 30;
      }
      // Course name tokens
      const courseParts = courseName.split(/\s+/).filter(w => w.length > 2);
      for (const cp of courseParts) {
        if (parts.some(p => p.includes(cp) || cp.includes(p))) score += 20;
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatch = cert;
      }
    }
    return { matchedCert: bestMatch, matchConfidence: bestScore };
  };

  const handleBulkFilesSelected = (files) => {
    const allCerts = getAllCertificates();
    const mapped = Array.from(files)
      .filter(f => ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'].includes(f.type))
      .map(file => {
        const { matchedCert, matchConfidence } = matchFileTocert(file.name, allCerts);
        return { file, matchedCert: matchConfidence >= 30 ? matchedCert : null, matchConfidence };
      });
    setBulkFiles(mapped);
  };

  const handleBulkUploadConfirm = async () => {
    const toUpload = bulkFiles.filter(bf => bf.matchedCert);
    if (toUpload.length === 0) {
      toast.error(t('adminToasts.NoFilesMatchedToCertificatesPleaseAssignCertificatesManually'));
      return;
    }
    setBulkUploading(true);
    setBulkUploadProgress({ done: 0, total: toUpload.length });
    let successCount = 0;
    for (let i = 0; i < toUpload.length; i++) {
      const { file, matchedCert } = toUpload[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axiosInstance.post(`/certificates/${matchedCert.id}/upload-file`, formData);
        successCount++;
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
      setBulkUploadProgress({ done: i + 1, total: toUpload.length });
    }
    toast.success(`Uploaded ${successCount}/${toUpload.length} certificate files`);
    setBulkUploading(false);
    setShowBulkUploadDialog(false);
    setBulkFiles([]);
    loadCertExpirationData(); // refresh to show updated badges
  };

  // Send expiration alert notifications
  const handleSendExpirationAlerts = async () => {
    setSendingAlerts(true);
    try {
      const res = await axiosInstance.post('/admin/check-expiring-certificates');
      const d = res.data;
      if (d.notifications_sent > 0 || d.emails_sent > 0) {
        toast.success(`Sent ${d.notifications_sent} in-app notifications and ${d.emails_sent} emails`);
      } else {
        toast.info(t('adminToasts.NoCertificatesExpiringAtDayThresholdsRightNow'));
      }
    } catch (error) {
      console.error('Failed to send alerts:', error);
      toast.error(t('adminToasts.FailedToSendExpirationAlerts'));
    } finally {
      setSendingAlerts(false);
    }
  };

  // Classroom Learning Handlers
  const handleAddStudent = async (e) => {
    e.preventDefault();
    
    if (!studentForm.craft) {
      toast.error(t('adminToasts.PleaseSelectACraftForTheStudent'));
      return;
    }
    
    try {
      await axiosInstance.post('/admin/classroom/students', {
        ...studentForm,
        companyId: user.companyId
      });
      
      toast.success(`Student ${studentForm.name} added successfully with login access`);
      setStudentForm({ name: '', email: '', phone: '', position: '', password: '', craft: '' });
      setShowStudentDialog(false);
      loadClassroomStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    }
  };

  const handleAddExistingUser = async () => {
    if (!selectedExistingUser) {
      toast.error(t('adminToasts.PleaseSelectAUser'));
      return;
    }

    try {
      await axiosInstance.post('/admin/classroom/add-existing', {
        userId: selectedExistingUser
      });
      
      toast.success(t('adminToasts.UserAddedToClassroomSuccessfully'));
      setShowAddExistingUserDialog(false);
      setSelectedExistingUser(null);
      loadClassroomStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user to classroom');
    }
  };

  const handleMarkPassed = async () => {
    if (!selectedStudent || !selectedCourse) {
      toast.error(t('adminToasts.PleaseSelectBothStudentAndCourse'));
      return;
    }

    try {
      await axiosInstance.post('/admin/classroom/mark-passed', {
        studentId: selectedStudent.id,
        courseId: selectedCourse.id,
        companyId: user.companyId
      });
      
      toast.success(`${selectedStudent.name} marked as passed for ${selectedCourse.title}. Certificate generated!`);
      setShowPassDialog(false);
      setSelectedStudent(null);
      setSelectedCourse(null);
      loadClassroomStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as passed');
    }
  };

  // Toolbox Talk Handlers
  const createToolboxTalk = async () => {
    try {
      setUploadingTalk(true);
      await axiosInstance.post('/admin/toolbox-talks', talkForm);
      toast.success(t('adminToasts.ToolboxTalkCreatedSuccessfully'));
      setShowCreateTalkDialog(false);
      setTalkForm({ title: '', description: '', content: '', category: '' });
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to create toolbox talk:', error);
      toast.error(error.response?.data?.detail || 'Failed to create toolbox talk');
    } finally {
      setUploadingTalk(false);
    }
  };

  const uploadToolboxTalkPdf = async () => {
    if (!selectedTalkPdf) {
      toast.error(t('adminToasts.PleaseSelectAPdfFile'));
      return;
    }

    try {
      setUploadingTalk(true);
      const formData = new FormData();
      formData.append('file', selectedTalkPdf);
      formData.append('title', talkForm.title);
      formData.append('description', talkForm.description || '');
      formData.append('category', talkForm.category || '');

      await axiosInstance.post('/admin/toolbox-talks/upload-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('adminToasts.ToolboxTalkPdfUploadedSuccessfully'));
      setShowUploadTalkDialog(false);
      setTalkForm({ title: '', description: '', content: '', category: '' });
      setSelectedTalkPdf(null);
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to upload toolbox talk PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploadingTalk(false);
    }
  };

  const deleteToolboxTalk = async (talkId) => {
    if (!window.confirm('Are you sure you want to delete this toolbox talk?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/toolbox-talks/${talkId}`);
      toast.success(t('adminToasts.ToolboxTalkDeletedSuccessfully'));
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to delete toolbox talk:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete toolbox talk');
    }
  };

  const viewToolboxTalk = (talk) => {
    setSelectedTalk(talk);
    setShowViewTalkDialog(true);
  };

  const downloadToolboxTalkPdf = async (talk) => {
    try {
      const response = await axiosInstance.get(`/toolbox-talks/${talk.id}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${talk.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('adminToasts.PdfDownloadedSuccessfully'));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const loadToolboxSessions = async () => {
    try {
      const response = await axiosInstance.get('/toolbox-talk-sessions');
      setToolboxSessions(response.data);
      setShowToolboxSessionsDialog(true);
    } catch (error) {
      console.error('Failed to load toolbox sessions:', error);
      toast.error(t('adminToasts.FailedToLoadSessions'));
    }
  };

  const handleExportAllToolboxPDFs = async () => {
    if (toolboxSessions.length === 0) {
      toast.error(t('adminToasts.NoToolboxSessionsToExport'));
      return;
    }

    toast.info(`Exporting ${toolboxSessions.length} toolbox talk PDFs...`);
    
    let successCount = 0;
    let failCount = 0;

    for (const session of toolboxSessions) {
      try {
        const talk = toolboxTalks.find(t => t.id === session.toolboxTalkId);
        const response = await axiosInstance.get(
          `/toolbox-talk-sessions/${session.id}/export`,
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `toolbox_talk_${talk?.title?.replace(/\s+/g, '_') || 'session'}_${new Date(session.conductedAt).toLocaleDateString().replace(/\//g, '-')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        successCount++;
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Failed to export session:', session.id, error);
        failCount++;
      }
    }

    if (failCount === 0) {
      toast.success(`Successfully exported all ${successCount} toolbox talk PDFs`);
    } else {
      toast.warning(`Exported ${successCount} PDFs, ${failCount} failed`);
    }
  };

  // ============================================
  // COURSE CREATOR FUNCTIONS
  // ============================================
  
  const loadCompanyCourses = async () => {
    try {
      const res = await axiosInstance.get('/company/courses');
      setCompanyCourses(res.data || []);
    } catch (error) {
      console.error('Failed to load company courses:', error);
    }
  };

  // Shared chunked video upload function
  const uploadVideoChunked = async (file) => {
    const CHUNK_SIZE = 800 * 1024; // 800KB chunks (under 1MB proxy limit)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const MAX_RETRIES = 3;

    // 1. Init upload session
    toast.info(`Uploading video (${(file.size / 1024 / 1024).toFixed(1)} MB)...`, { id: 'video-upload-progress', duration: 60000 });
    const initRes = await axiosInstance.post('/upload/video/init-chunked', {
      filename: file.name,
      fileSize: file.size,
      contentType: file.type || 'video/mp4'
    });
    const uploadId = initRes.data.uploadId;

    // 2. Upload chunks with retry
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      let success = false;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const chunkForm = new FormData();
          chunkForm.append('file', chunk, `chunk_${i}`);
          chunkForm.append('chunk_index', i.toString());
          chunkForm.append('total_chunks', totalChunks.toString());
          await axiosInstance.post(`/upload/video/chunk/${uploadId}`, chunkForm);
          success = true;
          break;
        } catch (err) {
          console.warn(`Chunk ${i+1}/${totalChunks} failed (attempt ${attempt}/${MAX_RETRIES}):`, err.message);
          if (attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 1000 * attempt)); // Backoff
          }
        }
      }
      if (!success) {
        toast.error(`Upload failed at ${Math.round((i/totalChunks)*100)}%. Please try again.`, { id: 'video-upload-progress' });
        throw new Error(`Chunk ${i+1} failed after ${MAX_RETRIES} retries`);
      }

      const pct = Math.round(((i + 1) / totalChunks) * 100);
      toast.info(`Uploading video... ${pct}%`, { id: 'video-upload-progress', duration: 60000 });
    }

    // 3. Complete upload
    toast.info(t('adminToasts.FinalizingVideo'), { id: 'video-upload-progress', duration: 30000 });
    const completeRes = await axiosInstance.post(`/upload/video/complete/${uploadId}`);
    toast.success(t('adminToasts.VideoUploaded'), { id: 'video-upload-progress' });
    return completeRes.data.videoUrl;
  };

  const handleCreateCourse = async () => {
    if (!courseForm.title || !courseForm.description || !courseForm.content) {
      toast.error(t('adminToasts.PleaseFillInAllRequiredFields'));
      return;
    }

    setCreatingCourse(true);
    try {
      let videoUrl = courseForm.videoUrl;

      // Upload video file if provided
      if (courseForm.videoFile) {
        try {
          videoUrl = await uploadVideoChunked(courseForm.videoFile);
        } catch (uploadError) {
          console.error('Video upload failed:', uploadError);
          toast.error(t('adminToasts.VideoUploadFailedPleaseTryAgain'));
          setCreatingCourse(false);
          return;
        }
      }

      const courseData = {
        title: courseForm.title,
        description: courseForm.description,
        category: courseForm.category,
        content: courseForm.content,
        duration: parseInt(courseForm.duration) || 30,
        videoUrl: videoUrl,
        imageUrl: courseForm.imageUrl,
        trainingType: courseForm.trainingType || 'company_private',
        certExpiry: courseForm.certExpiry ? parseInt(courseForm.certExpiry) : null,
        requiredForJSA: courseForm.requiredForJSA || false
      };

      const res = await axiosInstance.post('/company/courses', courseData);
      
      // Save quiz if any questions were added
      const validQuestions = quizQuestions.filter(q => 
        q.question.trim() && q.options.filter(o => o.trim()).length >= 2
      );
      
      if (validQuestions.length > 0 && res.data.id) {
        await axiosInstance.post(`/company/courses/${res.data.id}/quiz`, {
          questions: validQuestions
        });
      }

      toast.success(t('adminToasts.TrainingCreatedSuccessfully'));
      
      // Reset form
      setCourseForm({
        title: '',
        description: '',
        category: 'Safety',
        content: '',
        duration: 30,
        imageUrl: '',
        videoUrl: '',
        videoFile: null,
        videoFileName: '',
        trainingType: 'company_private',
        certExpiry: '',
        requiredForJSA: false
      });
      setQuizQuestions([{ question: '', options: ['', '', '', ''], correctAnswer: 0 }]);
      setShowCreateCourseDialog(false);
      
      // Reload courses
      loadData();
      loadCompanyCourses();
    } catch (error) {
      console.error('Failed to create course:', error);
      toast.error(error.response?.data?.detail || 'Failed to create training');
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      category: course.category || 'Safety',
      content: course.content || '',
      duration: course.duration || 30,
      imageUrl: course.imageUrl || '',
      videoUrl: course.videoUrl || '',
      videoFile: null,
      videoFileName: '',
      certExpiry: course.certExpiry || '',
      trainingType: course.trainingType || 'company_private'
    });
    setShowEditCourseDialog(true);
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse) return;

    setCreatingCourse(true);
    try {
      let videoUrl = courseForm.videoUrl;

      // Upload new video file if selected
      if (courseForm.videoFile) {
        try {
          videoUrl = await uploadVideoChunked(courseForm.videoFile);
        } catch (uploadError) {
          console.error('Video upload failed:', uploadError);
          toast.error(t('adminToasts.VideoUploadFailedPleaseTryAgain'));
          setCreatingCourse(false);
          return;
        }
      }

      const { videoFile, videoFileName, ...courseData } = courseForm;
      await axiosInstance.put(`/company/courses/${editingCourse.id}`, {
        ...courseData,
        videoUrl: videoUrl,
        duration: parseInt(courseForm.duration) || 30,
        certExpiry: courseForm.certExpiry ? parseInt(courseForm.certExpiry) : null,
        trainingType: courseForm.trainingType || 'company_private'
      });
      toast.success(t('adminToasts.CourseUpdatedSuccessfully'));
      setShowEditCourseDialog(false);
      setEditingCourse(null);
      loadData();
      loadCompanyCourses();
    } catch (error) {
      console.error('Failed to update course:', error);
      toast.error(error.response?.data?.detail || 'Failed to update course');
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!confirm('Are you sure you want to delete this course? This cannot be undone.')) {
      return;
    }

    try {
      await axiosInstance.delete(`/company/courses/${courseId}`);
      toast.success(t('adminToasts.CourseDeletedSuccessfully'));
      loadData();
      loadCompanyCourses();
    } catch (error) {
      console.error('Failed to delete course:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const handleOpenQuizDialog = (course) => {
    setEditingCourse(course);
    // Load existing quiz if any
    if (course.quiz && course.quiz.questions) {
      setQuizQuestions(course.quiz.questions);
    } else {
      setQuizQuestions([{ question: '', options: ['', '', '', ''], correctAnswer: 0 }]);
    }
    setShowQuizDialog(true);
  };

  const handleAddQuizQuestion = () => {
    setQuizQuestions([...quizQuestions, { question: '', options: ['', '', '', ''], correctAnswer: 0 }]);
  };

  const handleRemoveQuizQuestion = (index) => {
    if (quizQuestions.length > 1) {
      setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
    }
  };

  const handleQuizQuestionChange = (index, field, value) => {
    const updated = [...quizQuestions];
    if (field === 'question') {
      updated[index].question = value;
    } else if (field === 'correctAnswer') {
      updated[index].correctAnswer = parseInt(value);
    } else if (field.startsWith('option')) {
      const optionIndex = parseInt(field.replace('option', ''));
      updated[index].options[optionIndex] = value;
    }
    setQuizQuestions(updated);
  };

  const handleSaveQuiz = async () => {
    if (!editingCourse) return;

    // Validate quiz
    const validQuestions = quizQuestions.filter(q => 
      q.question.trim() && q.options.filter(o => o.trim()).length >= 2
    );

    if (validQuestions.length === 0) {
      toast.error(t('adminToasts.PleaseAddAtLeastOneValidQuestionWithOptions'));
      return;
    }

    try {
      await axiosInstance.post(`/company/courses/${editingCourse.id}/quiz`, {
        questions: validQuestions
      });
      toast.success(t('adminToasts.QuizSavedSuccessfully'));
      setShowQuizDialog(false);
      loadCompanyCourses();
    } catch (error) {
      console.error('Failed to save quiz:', error);
      toast.error(error.response?.data?.detail || 'Failed to save quiz');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNav user={user} onLogout={handleLogout} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('adminUI.LoadingTrainingManagement')}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('adminTraining.title')}</h1>
            <p className="text-gray-600 mt-1">{t('training.subtitle')}</p>
          </div>
          <Button
            onClick={() => navigate('/craft-management')}
            variant="outline"
            className="flex items-center space-x-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300"
          >
            <GraduationCap className="w-5 h-5" />
            <span>{t('adminTraining.manageCrafts')}</span>
          </Button>
        </div>

        <Tabs defaultValue="courses" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto">
            <TabsTrigger value="courses">
              <BookOpen className="w-4 h-4 mr-2" />
              {t('adminTraining.courses')}
            </TabsTrigger>
            <TabsTrigger value="create-course">
              <Plus className="w-4 h-4 mr-2" />
              {t('adminTraining.createCourse')}
            </TabsTrigger>
            <TabsTrigger value="classroom">
              <GraduationCap className="w-4 h-4 mr-2" />
              {t('adminTraining.classroom')}
            </TabsTrigger>
            <TabsTrigger value="certifications" onClick={() => !certExpirationData && loadCertExpirationData()}>
              <Clock className="w-4 h-4 mr-2" />
              {t('adminTraining.certExpiration')}
            </TabsTrigger>
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            {/* Required Courses Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('adminTraining.requiredCourses')}</CardTitle>
                    <CardDescription>{t('adminUI.ClickMultipleCoursesToMarkAsRequiredClickSaveChangesWhenDone')}</CardDescription>
                  </div>
                  <Button
                    onClick={handleSaveRequiredCourses}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t('common.save')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {courses.map((course) => (
                    <div
                      key={course.id}
                      onClick={() => handleToggleRequiredCourse(course.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedRequiredCourses.includes(course.id)
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{course.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{course.category} • {course.duration}</p>
                        </div>
                        {selectedRequiredCourses.includes(course.id) && (
                          <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 ml-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  {selectedRequiredCourses.length} of {courses.length} courses selected
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('adminTraining.allAvailableCourses')}</CardTitle>
                <CardDescription>{t('adminTraining.allCoursesDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {courses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>{t('adminUI.NoCoursesAvailable')}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('trainingManager.trainingTitleCol')}</TableHead>
                        <TableHead>{t('trainingManager.categoryCol')}</TableHead>
                        <TableHead>{t('trainingManager.durationCol')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {courses.map(course => (
                        <TableRow key={course.id}>
                          <TableCell className="font-medium">{course.title}</TableCell>
                          <TableCell>
                            <Badge className="bg-orange-100 text-orange-800">{course.category}</Badge>
                          </TableCell>
                          <TableCell>{course.duration} min</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Create Course Tab */}
          <TabsContent value="create-course" className="space-y-6">
            {/* Create New Course */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('adminUI.CreateCompanyCourse')}</CardTitle>
                    <CardDescription>
                      Create custom training courses for your company. These courses are private and won't transfer if workers leave.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setCourseForm({
                        title: '',
                        description: '',
                        category: 'Safety',
                        content: '',
                        duration: 30,
                        imageUrl: '',
                        videoUrl: ''
                      });
                      setShowCreateCourseDialog(true);
                    }}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Course
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {companyCourses.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">{t('adminUI.NoCompanyCoursesYet')}</p>
                    <p className="text-sm">{t('adminUI.CreateYourFirstCustomTrainingCourse')}</p>
                    <Button
                      onClick={() => setShowCreateCourseDialog(true)}
                      className="mt-4 bg-orange-600 hover:bg-orange-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Course
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {companyCourses.map((course) => (
                      <div
                        key={course.id}
                        className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">{course.title}</h3>
                              <Badge variant="outline" className={`text-xs ${course.trainingType === 'company_transferable' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}`}>
                                {course.trainingType === 'company_transferable' ? 'Transferable' : 'Company Private'}
                              </Badge>
                              {course.quiz && <Badge className="bg-green-100 text-green-800 text-xs">{t('adminUI.HasQuiz')}</Badge>}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>{course.category}</span>
                              <span>•</span>
                              <span>{course.duration} min</span>
                              {course.videoUrl && (
                                <>
                                  <span>•</span>
                                  <span className="text-blue-600">{t('adminUI.HasVideo')}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenQuizDialog(course)}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Quiz
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditCourse(course)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteCourse(course.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Course Categories Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('adminUI.CourseCategories')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-semibold text-blue-900">{t('adminUI.Safety')}</p>
                    <p className="text-blue-700">{t('adminUI.GeneralSafetyProceduresPpeEmergencyResponse')}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="font-semibold text-green-900">{t('adminUI.Equipment')}</p>
                    <p className="text-green-700">{t('adminUI.ToolOperationMachineryVehicleSafety')}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="font-semibold text-purple-900">{t('adminUI.Compliance')}</p>
                    <p className="text-purple-700">{t('adminUI.OshaRegulationsCompanyPoliciesDocumentation')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Classroom Learning Tab */}
          <TabsContent value="classroom" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" />
                      Classroom Learning
                    </CardTitle>
                    <CardDescription>{t('adminUI.RegisterStudentsAndMarkThemAsPassedForInPersonTraining')}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowStudentDialog(true)}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add New Student
                    </Button>
                    <Button
                      onClick={() => setShowAddExistingUserDialog(true)}
                      variant="outline"
                      className="border-gray-800 text-gray-800 hover:bg-gray-100"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Add Existing User
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {classroomStudents.length === 0 ? (
                  <div className="text-center py-8">
                    <GraduationCap className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">{t('adminUI.NoClassroomStudentsRegisteredYet')}</p>
                    <Button
                      onClick={() => setShowStudentDialog(true)}
                      variant="outline"
                      className="border-orange-400 text-orange-600 hover:bg-orange-50"
                    >
                      Add Your First Student
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {classroomStudents.map((student) => (
                      <div key={student.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg">{student.name}</h4>
                            <p className="text-slate-600">{student.email}</p>
                            {student.phone && <p className="text-sm text-slate-500">Phone: {student.phone}</p>}
                            {student.position && <p className="text-sm text-slate-500">Position: {student.position}</p>}
                            
                            {/* Certificates */}
                            {student.certificates && student.certificates.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-green-600 mb-2">{t('adminUI.CertificatesEarned')}</p>
                                <div className="space-y-2">
                                  {student.certificates.map((cert) => (
                                    <div key={cert.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2">
                                      <span className="inline-flex items-center text-sm font-medium text-green-800">
                                        <Award className="w-4 h-4 mr-2" />
                                        {cert.courseName}
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={async () => {
                                          try {
                                            const response = await axiosInstance.get(`/certificates/${cert.id}/download`, {
                                              responseType: 'blob'
                                            });
                                            
                                            const url = window.URL.createObjectURL(new Blob([response.data]));
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute('download', `${cert.courseName.replace(/[^a-z0-9]/gi, '_')}_Certificate.txt`);
                                            document.body.appendChild(link);
                                            link.click();
                                            link.remove();
                                            window.URL.revokeObjectURL(url);
                                            
                                            toast.success(t('adminToasts.CertificateDownloadedSuccessfully'));
                                          } catch (error) {
                                            console.error('Failed to download certificate:', error);
                                            toast.error(t('adminToasts.FailedToDownloadCertificate'));
                                          }
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white"
                                      >
                                        <Download className="w-3 h-3 mr-1" />
                                        Download
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <Button
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowPassDialog(true);
                            }}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Passed
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certifications Tab */}
          <TabsContent value="certifications" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-600" />
                      Certification Expiration Tracker
                    </CardTitle>
                    <CardDescription>{t('adminUI.MonitorEmployeeCertificationsAndGetAlertsForUpcomingExpirati')}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="send-expiration-alerts-btn"
                      onClick={handleSendExpirationAlerts}
                      disabled={sendingAlerts}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                    >
                      {sendingAlerts ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Bell className="w-4 h-4 mr-2" />
                          Send Expiration Alerts
                        </>
                      )}
                    </Button>
                    <Button 
                      data-testid="bulk-upload-cert-files-btn"
                      onClick={() => {
                        setBulkFiles([]);
                        setShowBulkUploadDialog(true);
                      }}
                      variant="outline"
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Bulk Upload Files
                    </Button>
                    <Button 
                      onClick={loadCertExpirationData} 
                      variant="outline"
                      disabled={loadingCertExpiration}
                    >
                      {loadingCertExpiration ? 'Loading...' : 'Refresh'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingCertExpiration ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-2"></div>
                    <p className="text-gray-500">{t('adminUI.LoadingCertificationData')}</p>
                  </div>
                ) : certExpirationData ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <Card 
                        className={`cursor-pointer transition-all ${certExpirationFilter === 'expired' ? 'ring-2 ring-red-500' : ''}`}
                        onClick={() => setCertExpirationFilter('expired')}
                      >
                        <CardContent className="p-4 text-center">
                          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-red-600">{certExpirationData.summary.expired}</p>
                          <p className="text-xs text-gray-500">{t('adminUI.Expired')}</p>
                        </CardContent>
                      </Card>
                      
                      <Card 
                        className={`cursor-pointer transition-all ${certExpirationFilter === 'expiring_soon' ? 'ring-2 ring-orange-500' : ''}`}
                        onClick={() => setCertExpirationFilter('expiring_soon')}
                      >
                        <CardContent className="p-4 text-center">
                          <AlertTriangle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-orange-600">{certExpirationData.summary.expiringSoon}</p>
                          <p className="text-xs text-gray-500">{"< 30 Days"}</p>
                        </CardContent>
                      </Card>
                      
                      <Card 
                        className={`cursor-pointer transition-all ${certExpirationFilter === 'expiring_later' ? 'ring-2 ring-yellow-500' : ''}`}
                        onClick={() => setCertExpirationFilter('expiring_later')}
                      >
                        <CardContent className="p-4 text-center">
                          <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-yellow-600">{certExpirationData.summary.expiringLater}</p>
                          <p className="text-xs text-gray-500">30-90 Days</p>
                        </CardContent>
                      </Card>
                      
                      <Card 
                        className={`cursor-pointer transition-all ${certExpirationFilter === 'valid' ? 'ring-2 ring-green-500' : ''}`}
                        onClick={() => setCertExpirationFilter('valid')}
                      >
                        <CardContent className="p-4 text-center">
                          <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-green-600">{certExpirationData.summary.valid}</p>
                          <p className="text-xs text-gray-500">{"> 90 Days"}</p>
                        </CardContent>
                      </Card>
                      
                      <Card 
                        className={`cursor-pointer transition-all ${certExpirationFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
                        onClick={() => setCertExpirationFilter('all')}
                      >
                        <CardContent className="p-4 text-center">
                          <Award className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-blue-600">{certExpirationData.summary.total}</p>
                          <p className="text-xs text-gray-500">{t('adminUI.Total')}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Certificates Table */}
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('adminUI.Employee')}</TableHead>
                            <TableHead>{t('adminUI.Course')}</TableHead>
                            <TableHead>{t('adminUI.Issued')}</TableHead>
                            <TableHead>{t('adminUI.Expires')}</TableHead>
                            <TableHead>{t('adminUI.Status')}</TableHead>
                            <TableHead>File</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getFilteredCertificates().length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                No certificates found for this filter
                              </TableCell>
                            </TableRow>
                          ) : (
                            getFilteredCertificates().map((cert) => (
                              <TableRow key={cert.id || cert.certificateId}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium">{cert.userName}</p>
                                    <p className="text-xs text-gray-500">{cert.userEmail}</p>
                                  </div>
                                </TableCell>
                                <TableCell>{cert.courseName}</TableCell>
                                <TableCell>{formatDate(cert.issuedAt)}</TableCell>
                                <TableCell>{formatDate(cert.expiresAt)}</TableCell>
                                <TableCell>
                                  {getStatusBadge(cert.status, cert.daysUntilExpiry)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      data-testid={`cert-download-${cert.id}`}
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs border-green-300 text-green-700 hover:bg-green-50"
                                      onClick={async () => {
                                        try {
                                          const response = await axiosInstance.get(`/certificates/${cert.id}/download`, {
                                            responseType: 'blob'
                                          });
                                          const url = window.URL.createObjectURL(new Blob([response.data]));
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.setAttribute('download', `${(cert.userName || 'Certificate').replace(/[^a-z0-9]/gi, '_')}_${(cert.courseName || 'Cert').replace(/[^a-z0-9]/gi, '_')}.pdf`);
                                          document.body.appendChild(link);
                                          link.click();
                                          link.remove();
                                          window.URL.revokeObjectURL(url);
                                        } catch (error) {
                                          console.error('Download failed:', error);
                                          toast.error(t('adminToasts.FailedToDownloadCertificate'));
                                        }
                                      }}
                                    >
                                      <Download className="w-3 h-3 mr-1" />
                                      Download
                                    </Button>
                                    {cert.attachedFileId && (
                                      <Button
                                        data-testid={`cert-view-file-${cert.id}`}
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-blue-600 hover:text-blue-800"
                                        title={`View ${cert.attachedFileName || 'attached file'}`}
                                        onClick={() => {
                                          window.open(`${axiosInstance.defaults.baseURL}/certificates/${cert.id}/download-file?token=${localStorage.getItem('token')}`, '_blank');
                                        }}
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">{t('adminUI.ClickTheTabToLoadCertificationExpirationData')}</p>
                    <Button onClick={loadCertExpirationData}>{t('adminUI.LoadData')}</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Note: Toolbox Talks moved to dedicated page at /admin/toolbox */}

        {/* Certificate File Upload Dialog */}
        <Dialog open={showCertUploadDialog} onOpenChange={setShowCertUploadDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange-600" />
                {selectedCertForUpload?.attachedFileId ? 'Replace Certificate File' : 'Upload Certificate File'}
              </DialogTitle>
              <DialogDescription>
                Attach the actual certificate file for <strong>{selectedCertForUpload?.userName}</strong> - <strong>{selectedCertForUpload?.courseName}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="cert-file-input">Select File (PDF, JPG, or PNG)</Label>
                <Input
                  id="cert-file-input"
                  data-testid="cert-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="mt-2"
                  onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                />
              </div>
              {certFile && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-orange-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{certFile.name}</p>
                    <p className="text-xs text-gray-500">{(certFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              )}
              {selectedCertForUpload?.attachedFileName && (
                <p className="text-xs text-gray-500">
                  Current file: {selectedCertForUpload.attachedFileName}. Uploading a new file will replace it.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCertUploadDialog(false)}>
                Cancel
              </Button>
              <Button
                data-testid="cert-upload-submit"
                onClick={handleCertFileUpload}
                disabled={!certFile || uploadingCertFile}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                {uploadingCertFile ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Certificate File Upload Dialog */}
        <Dialog open={showBulkUploadDialog} onOpenChange={(open) => { if (!bulkUploading) setShowBulkUploadDialog(open); }}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange-600" />
                Bulk Upload Certificate Files
              </DialogTitle>
              <DialogDescription>
                Drop multiple files. The system auto-matches by worker name, course name, or certificate ID in the filename.
              </DialogDescription>
            </DialogHeader>

            {bulkFiles.length === 0 ? (
              <div
                data-testid="bulk-drop-zone"
                className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center cursor-pointer hover:border-orange-400 transition-colors"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleBulkFilesSelected(e.dataTransfer.files); }}
                onClick={() => document.getElementById('bulk-file-input').click()}
              >
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="font-medium text-gray-700">{t('adminUI.DragDropCertificateFilesHere')}</p>
                <p className="text-sm text-gray-500 mt-1">or click to browse — PDF, JPG, PNG accepted</p>
                <p className="text-xs text-gray-400 mt-3">Tip: Name files like <span className="font-mono bg-gray-100 px-1 rounded">{t('adminUI.JohnDoeOshaPdf')}</span> or <span className="font-mono bg-gray-100 px-1 rounded">{t('adminUI.CertXxxxxxxxPdf')}</span> for best auto-matching</p>
                <input
                  id="bulk-file-input"
                  data-testid="bulk-file-input"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => handleBulkFilesSelected(e.target.files)}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">
                    {bulkFiles.filter(bf => bf.matchedCert).length} of {bulkFiles.length} files matched
                  </p>
                  {!bulkUploading && (
                    <Button variant="ghost" size="sm" onClick={() => setBulkFiles([])}>
                      <X className="w-4 h-4 mr-1" /> Clear All
                    </Button>
                  )}
                </div>

                {bulkUploading && (
                  <div className="space-y-2 mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full transition-all"
                        style={{ width: `${(bulkUploadProgress.done / bulkUploadProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Uploading {bulkUploadProgress.done}/{bulkUploadProgress.total}...
                    </p>
                  </div>
                )}

                <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-1">
                  {bulkFiles.map((bf, idx) => (
                    <div key={idx} data-testid={`bulk-file-row-${idx}`} className={`flex items-center gap-3 p-3 rounded-lg border ${bf.matchedCert ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
                      <FileText className={`w-5 h-5 flex-shrink-0 ${bf.matchedCert ? 'text-green-600' : 'text-yellow-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{bf.file.name}</p>
                        <p className="text-xs text-gray-500">{(bf.file.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <div className="flex-shrink-0 w-56">
                        <select
                          data-testid={`bulk-match-select-${idx}`}
                          className="w-full text-xs border rounded px-2 py-1.5 bg-white"
                          value={bf.matchedCert?.id || ''}
                          disabled={bulkUploading}
                          onChange={(e) => {
                            const certId = e.target.value;
                            const cert = certId ? getAllCertificates().find(c => c.id === certId) : null;
                            setBulkFiles(prev => prev.map((item, i) => i === idx ? { ...item, matchedCert: cert } : item));
                          }}
                        >
                          <option value="">-- No Match --</option>
                          {getAllCertificates().map(c => (
                            <option key={c.id} value={c.id}>{c.userName} — {c.courseName}</option>
                          ))}
                        </select>
                      </div>
                      {bf.matchedCert && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => { if (!bulkUploading) { setShowBulkUploadDialog(false); setBulkFiles([]); } }}>
                Cancel
              </Button>
              {bulkFiles.length > 0 && (
                <Button
                  data-testid="bulk-upload-confirm-btn"
                  onClick={handleBulkUploadConfirm}
                  disabled={bulkUploading || bulkFiles.filter(bf => bf.matchedCert).length === 0}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                >
                  {bulkUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {bulkFiles.filter(bf => bf.matchedCert).length} Files
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialogs below */}
        <div className="hidden">
          {/* Placeholder to maintain structure */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('adminTraining.toolboxTalks')}</CardTitle>
                    <CardDescription>{t('adminTraining.toolboxTalksDesc')}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowCreateTalkDialog(true)}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Talk
                    </Button>
                    <Button
                      onClick={() => setShowUploadTalkDialog(true)}
                      variant="outline"
                      className="border-gray-800 text-gray-800 hover:bg-gray-100"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload PDF
                    </Button>
                    <Button
                      onClick={loadToolboxSessions}
                      variant="outline"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      View Sessions
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {toolboxTalks.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">{t('adminUI.NoToolboxTalksCreatedYet')}</p>
                    <Button
                      onClick={() => setShowCreateTalkDialog(true)}
                      variant="outline"
                      className="border-orange-400 text-orange-600 hover:bg-orange-50"
                    >
                      Create Your First Talk
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {toolboxTalks.map((talk) => (
                      <div key={talk.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg">{talk.title}</h4>
                            {talk.description && (
                              <p className="text-sm text-gray-600 mt-1">{talk.description}</p>
                            )}
                            {talk.category && (
                              <Badge variant="outline" className="mt-2">{talk.category}</Badge>
                            )}
                            {talk.pdfPath && (
                              <Badge className="mt-2 ml-2 bg-red-100 text-red-700">PDF</Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {talk.pdfPath ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadToolboxTalkPdf(talk)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewToolboxTalk(talk)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteToolboxTalk(talk.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
        </div>

        {/* Add Student Dialog */}
        <Dialog open={showStudentDialog} onOpenChange={setShowStudentDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('adminUI.AddClassroomStudent')}</DialogTitle>
              <DialogDescription>{t('adminUI.RegisterANewStudentForInPersonTraining')}</DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleAddStudent} className="space-y-4">
              <div>
                <Label>{t('adminUI.FullName')}</Label>
                <Input
                  placeholder="John Doe"
                  value={studentForm.name}
                  onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label>{t('adminUI.EmailAddress')}</Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={studentForm.email}
                  onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label>{t('adminUI.Password')}</Label>
                <Input
                  type="password"
                  placeholder="Temporary password"
                  value={studentForm.password}
                  onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label>{t('adminUI.Phone')}</Label>
                <Input
                  placeholder="(555) 123-4567"
                  value={studentForm.phone}
                  onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })}
                />
              </div>
              
              <div>
                <Label>{t('adminUI.PositionTitle')}</Label>
                <Input
                  placeholder="e.g., Apprentice"
                  value={studentForm.position}
                  onChange={(e) => setStudentForm({ ...studentForm, position: e.target.value })}
                />
              </div>

              <div>
                <Label>{t('adminUI.Craft')}</Label>
                <Select
                  value={studentForm.craft}
                  onValueChange={(value) => setStudentForm({ ...studentForm, craft: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select craft" />
                  </SelectTrigger>
                  <SelectContent>
                    {crafts.map(craft => (
                      <SelectItem key={craft.id} value={craft.id}>{craft.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowStudentDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">{t('adminUI.AddStudent')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Existing User Dialog */}
        <Dialog open={showAddExistingUserDialog} onOpenChange={setShowAddExistingUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminTraining.addExistingUser')}</DialogTitle>
              <DialogDescription>{t('adminUI.SelectAUserFromYourCompanyToAddToClassroomLearning')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Select
                value={selectedExistingUser}
                onValueChange={setSelectedExistingUser}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.role === 'worker' || u.role === 'learner').map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddExistingUserDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddExistingUser}>{t('adminUI.AddToClassroom')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark as Passed Dialog */}
        <Dialog open={showPassDialog} onOpenChange={setShowPassDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminTraining.markAsPassed')}</DialogTitle>
              <DialogDescription>
                Select the course that {selectedStudent?.name} has completed
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Student:</strong> {selectedStudent?.name}<br />
                  <strong>Email:</strong> {selectedStudent?.email}
                </p>
              </div>
              <div>
                <Label>{t('adminUI.SelectCourse')}</Label>
                <Select
                  value={selectedCourse?.id}
                  onValueChange={(value) => {
                    const course = courses.find(c => c.id === value);
                    setSelectedCourse(course);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPassDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleMarkPassed} className="bg-green-600 hover:bg-green-700">{t('adminUI.MarkAsPassedGenerateCertificate')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Toolbox Talk Dialog */}
        <Dialog open={showCreateTalkDialog} onOpenChange={setShowCreateTalkDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminTraining.createTextToolbox')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateANewToolboxTalkWithTextContent')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('adminUI.Title')}</Label>
                <Input
                  value={talkForm.title}
                  onChange={(e) => setTalkForm({ ...talkForm, title: e.target.value })}
                  placeholder="e.g., Ladder Safety"
                />
              </div>
              <div>
                <Label>{t('adminUI.Description')}</Label>
                <Input
                  value={talkForm.description}
                  onChange={(e) => setTalkForm({ ...talkForm, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>
              <div>
                <Label>{t('adminUI.Category')}</Label>
                <Input
                  value={talkForm.category}
                  onChange={(e) => setTalkForm({ ...talkForm, category: e.target.value })}
                  placeholder="e.g., Fall Protection"
                />
              </div>
              <div>
                <Label>{t('adminUI.Content')}</Label>
                <Textarea
                  value={talkForm.content}
                  onChange={(e) => setTalkForm({ ...talkForm, content: e.target.value })}
                  placeholder="Enter the full content of the toolbox talk..."
                  rows={10}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateTalkDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createToolboxTalk} disabled={uploadingTalk}>
                {uploadingTalk ? 'Creating...' : 'Create Toolbox Talk'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload PDF Toolbox Talk Dialog */}
        <Dialog open={showUploadTalkDialog} onOpenChange={setShowUploadTalkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminTraining.uploadPdfToolbox')}</DialogTitle>
              <DialogDescription>{t('adminUI.UploadAPdfFileForYourToolboxTalk')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('adminUI.Title')}</Label>
                <Input
                  value={talkForm.title}
                  onChange={(e) => setTalkForm({ ...talkForm, title: e.target.value })}
                  placeholder="e.g., Electrical Safety"
                />
              </div>
              <div>
                <Label>{t('adminUI.Description')}</Label>
                <Input
                  value={talkForm.description}
                  onChange={(e) => setTalkForm({ ...talkForm, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>
              <div>
                <Label>{t('adminUI.Category')}</Label>
                <Input
                  value={talkForm.category}
                  onChange={(e) => setTalkForm({ ...talkForm, category: e.target.value })}
                  placeholder="e.g., Electrical"
                />
              </div>
              <div>
                <Label>{t('adminUI.PdfFile')}</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setSelectedTalkPdf(e.target.files[0])}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadTalkDialog(false)}>
                Cancel
              </Button>
              <Button onClick={uploadToolboxTalkPdf} disabled={uploadingTalk}>
                {uploadingTalk ? 'Uploading...' : 'Upload PDF'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Toolbox Talk Dialog */}
        <Dialog open={showViewTalkDialog} onOpenChange={setShowViewTalkDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTalk?.title}</DialogTitle>
              {selectedTalk?.description && (
                <DialogDescription>{selectedTalk.description}</DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-4">
              {selectedTalk?.category && (
                <Badge variant="outline">{selectedTalk.category}</Badge>
              )}
              <div className="prose max-w-none">
                <p className="whitespace-pre-wrap">{selectedTalk?.content}</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowViewTalkDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toolbox Sessions Dialog */}
        <Dialog open={showToolboxSessionsDialog} onOpenChange={setShowToolboxSessionsDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ToolboxTalkSessions')}</DialogTitle>
              <DialogDescription>{t('adminUI.ViewAllConductedToolboxTalkSessions')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {toolboxSessions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t('adminUI.NoSessionsConductedYet')}</p>
              ) : (
                <>
                  <Button onClick={handleExportAllToolboxPDFs} className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Export All Sessions as PDFs
                  </Button>
                  <div className="space-y-3">
                    {toolboxSessions.map((session) => {
                      const talk = toolboxTalks.find(t => t.id === session.toolboxTalkId);
                      return (
                        <div key={session.id} className="border rounded-lg p-3">
                          <p className="font-semibold">{talk?.title || 'Unknown Talk'}</p>
                          <p className="text-sm text-gray-600">
                            Conducted on {new Date(session.conductedAt).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-gray-600">
                            Attendees: {session.attendees?.length || 0}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setShowToolboxSessionsDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Course Dialog */}
        <Dialog open={showCreateCourseDialog} onOpenChange={setShowCreateCourseDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.CreateNewTraining')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateACustomTrainingCourseForYourCompany')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Training Details Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Training Details
                </h3>
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('adminUI.TrainingTitle')}</Label>
                      <Input
                        placeholder="e.g., Forklift Safety Basics"
                        value={courseForm.title}
                        onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{t('adminUI.Category')}</Label>
                      <Select
                        value={courseForm.category}
                        onValueChange={(v) => setCourseForm({ ...courseForm, category: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Safety">Safety</SelectItem>
                          <SelectItem value="Equipment">Equipment</SelectItem>
                          <SelectItem value="Compliance">Compliance</SelectItem>
                          <SelectItem value="Hazard Communication">Hazard Communication</SelectItem>
                          <SelectItem value="Emergency Response">Emergency Response</SelectItem>
                          <SelectItem value="PPE">PPE</SelectItem>
                          <SelectItem value="Fall Protection">Fall Protection</SelectItem>
                          <SelectItem value="Electrical">Electrical</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('adminUI.Description')}</Label>
                      <Textarea
                        placeholder="Brief description of what this training covers..."
                        value={courseForm.description}
                        onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Duration (minutes)</Label>
                      <Input
                        type="number"
                        placeholder="30"
                        value={courseForm.duration}
                        onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{t('adminUI.TrainingContent')}</Label>
                    <Textarea
                      placeholder="Enter the full training content here. You can include multiple sections, bullet points, step-by-step instructions, etc..."
                      value={courseForm.content}
                      onChange={(e) => setCourseForm({ ...courseForm, content: e.target.value })}
                      rows={6}
                    />
                  </div>
                </div>
              </div>

              {/* Video Upload Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Video Upload (Optional)
                </h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('course-video-upload')?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-orange-400', 'bg-orange-50'); }}
                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50');
                      const file = e.dataTransfer.files[0];
                      if (file && file.type.startsWith('video/')) {
                        setCourseForm({ ...courseForm, videoFile: file, videoFileName: file.name });
                      }
                    }}
                  >
                    <input
                      id="course-video-upload"
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCourseForm({ ...courseForm, videoFile: file, videoFileName: file.name });
                        }
                      }}
                    />
                    {courseForm.videoFileName ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span>{courseForm.videoFileName}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCourseForm({ ...courseForm, videoFile: null, videoFileName: '' });
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">{t('adminUI.DragAndDropAVideoFileHere')}</p>
                        <p className="text-xs text-gray-400 mt-1">or click to browse (MP4, MOV, WebM)</p>
                      </>
                    )}
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs text-gray-500">{t('adminUI.OrEnterAVideoUrl')}</Label>
                    <Input
                      placeholder="https://youtube.com/... or https://vimeo.com/..."
                      value={courseForm.videoUrl}
                      onChange={(e) => setCourseForm({ ...courseForm, videoUrl: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Certificate Settings Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Certificate Settings
                </h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('adminUI.TrainingType')}</Label>
                      <Select
                        value={courseForm.trainingType || 'company_private'}
                        onValueChange={(v) => setCourseForm({ ...courseForm, trainingType: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="company_private">Company Private (does not transfer)</SelectItem>
                          <SelectItem value="company_transferable">Company Transferable</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">{t('adminUI.PrivateTrainingsStayWithYourCompanyOnly')}</p>
                    </div>
                    <div>
                      <Label>Certificate Expiry (months)</Label>
                      <Input
                        type="number"
                        placeholder="12"
                        value={courseForm.certExpiry || ''}
                        onChange={(e) => setCourseForm({ ...courseForm, certExpiry: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('adminUI.LeaveBlankForNoExpiration')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Required for JSA Toggle */}
              <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex-1">
                  <Label className="text-base font-semibold text-orange-900">{t('adminUI.RequiredForJsaSigning')}</Label>
                  <p className="text-sm text-orange-700 mt-1">
                    Workers must complete this training before signing any JSA. Incomplete or expired training will show a warning.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCourseForm({ ...courseForm, requiredForJSA: !courseForm.requiredForJSA })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    courseForm.requiredForJSA ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      courseForm.requiredForJSA ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Knowledge Quiz Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Knowledge Quiz Questions (Optional)
                </h3>
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  {quizQuestions.map((q, qIndex) => (
                    <div key={qIndex} className="p-3 bg-white rounded border">
                      <div className="flex items-start justify-between mb-2">
                        <Label className="text-sm font-medium">Question {qIndex + 1}</Label>
                        {quizQuestions.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                            onClick={() => handleRemoveQuizQuestion(qIndex)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <Input
                        placeholder="Enter your question..."
                        value={q.question}
                        onChange={(e) => handleQuizQuestionChange(qIndex, 'question', e.target.value)}
                        className="mb-2"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        {q.options.map((opt, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={`create-correct-${qIndex}`}
                              checked={q.correctAnswer === optIndex}
                              onChange={() => handleQuizQuestionChange(qIndex, 'correctAnswer', optIndex)}
                              className="w-4 h-4 text-orange-600"
                            />
                            <Input
                              placeholder={`Option ${optIndex + 1}`}
                              value={opt}
                              onChange={(e) => handleQuizQuestionChange(qIndex, `option${optIndex}`, e.target.value)}
                              className="flex-1 h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddQuizQuestion}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Question
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateCourseDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateCourse}
                disabled={creatingCourse}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {creatingCourse ? 'Creating...' : 'Create Training'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Course Dialog */}
        <Dialog open={showEditCourseDialog} onOpenChange={setShowEditCourseDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.EditCourse')}</DialogTitle>
              <DialogDescription>{t('adminUI.UpdateCourseDetails')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('adminUI.CourseTitle')}</Label>
                  <Input
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t('adminUI.Category')}</Label>
                  <Select
                    value={courseForm.category}
                    onValueChange={(v) => setCourseForm({ ...courseForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Safety">Safety</SelectItem>
                      <SelectItem value="Equipment">Equipment</SelectItem>
                      <SelectItem value="Compliance">Compliance</SelectItem>
                      <SelectItem value="Hazard Communication">Hazard Communication</SelectItem>
                      <SelectItem value="Emergency Response">Emergency Response</SelectItem>
                      <SelectItem value="PPE">PPE</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{t('adminUI.TrainingType')}</Label>
                <Select
                  value={courseForm.trainingType || 'company_private'}
                  onValueChange={(v) => setCourseForm({ ...courseForm, trainingType: v })}
                >
                  <SelectTrigger data-testid="edit-training-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company_private">Company Private (does not transfer)</SelectItem>
                    <SelectItem value="company_transferable">Company Transferable</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('adminUI.Description')}</Label>
                <Textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <Label>{t('adminUI.CourseContent')}</Label>
                <Textarea
                  value={courseForm.content}
                  onChange={(e) => setCourseForm({ ...courseForm, content: e.target.value })}
                  rows={8}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{t('adminUI.ImageUrl')}</Label>
                  <Input
                    value={courseForm.imageUrl}
                    onChange={(e) => setCourseForm({ ...courseForm, imageUrl: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={courseForm.duration}
                    onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Cert Expiry (months)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 12, 24, 36"
                    value={courseForm.certExpiry}
                    onChange={(e) => setCourseForm({ ...courseForm, certExpiry: e.target.value })}
                  />
                </div>
              </div>

              {/* Video Upload / URL */}
              <div>
                <Label>{t('adminUI.TrainingVideo')}</Label>
                <div className="mt-2 space-y-3">
                  {/* File upload */}
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-shrink-0"
                      onClick={() => document.getElementById('edit-video-file-input').click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {courseForm.videoFile ? 'Change File' : 'Upload Video File'}
                    </Button>
                    <input
                      id="edit-video-file-input"
                      data-testid="edit-video-file-input"
                      type="file"
                      accept="video/mp4,video/avi,video/mov,video/wmv,video/mkv,video/webm"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCourseForm({ ...courseForm, videoFile: file, videoFileName: file.name });
                        }
                      }}
                    />
                    {courseForm.videoFile ? (
                      <span className="text-sm text-green-600 truncate">{courseForm.videoFileName}</span>
                    ) : courseForm.videoUrl ? (
                      <span className="text-sm text-gray-500 truncate">Current: {courseForm.videoUrl.includes('/stream/video/') ? 'Uploaded video' : courseForm.videoUrl}</span>
                    ) : (
                      <span className="text-sm text-gray-400">{t('adminUI.NoVideo')}</span>
                    )}
                  </div>
                  {/* Or paste URL */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{t('adminUI.OrPasteUrl')}</span>
                    <Input
                      placeholder="https://youtube.com/embed/... or https://player.vimeo.com/video/..."
                      value={courseForm.videoFile ? '' : (courseForm.videoUrl || '')}
                      disabled={!!courseForm.videoFile}
                      onChange={(e) => setCourseForm({ ...courseForm, videoUrl: e.target.value })}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditCourseDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateCourse}
                disabled={creatingCourse}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {creatingCourse ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Editor Dialog */}
        <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.CourseQuizEditor')}</DialogTitle>
              <DialogDescription>
                Create or edit quiz questions for: {editingCourse?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {quizQuestions.map((q, qIndex) => (
                <div key={qIndex} className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <Label className="text-base font-semibold">Question {qIndex + 1}</Label>
                    {quizQuestions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleRemoveQuizQuestion(qIndex)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <Input
                    placeholder="Enter your question..."
                    value={q.question}
                    onChange={(e) => handleQuizQuestionChange(qIndex, 'question', e.target.value)}
                    className="mb-3"
                  />
                  
                  <div className="space-y-2">
                    {q.options.map((opt, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`correct-${qIndex}`}
                          checked={q.correctAnswer === optIndex}
                          onChange={() => handleQuizQuestionChange(qIndex, 'correctAnswer', optIndex)}
                          className="w-4 h-4 text-orange-600"
                        />
                        <Input
                          placeholder={`Option ${optIndex + 1}`}
                          value={opt}
                          onChange={(e) => handleQuizQuestionChange(qIndex, `option${optIndex}`, e.target.value)}
                          className="flex-1"
                        />
                        {q.correctAnswer === optIndex && (
                          <Badge className="bg-green-100 text-green-800">{t('adminUI.Correct')}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={handleAddQuizQuestion}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQuizDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveQuiz}
                className="bg-orange-600 hover:bg-orange-700"
              >{t('adminUI.SaveQuiz')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminTraining;
